import { CSSProperties } from 'react';
import styled, { css } from 'styled-components';

import type { ContainerProps, MaxWidthValueType } from './Container.types';

const fixedMenuWidth = '73px';

const container = {
  root: {
    paddingInlineEnd: '0px',
    paddingInlineStart: '0px',
  },
  maxWidthSidebarLayout: {
    maxWidth: '1718px',
  },
  looseSm: {
    paddingLeft: '24px',
    paddingRight: '24px',
  },
  tightSm: {
    paddingLeft: '48px',
    paddingRight: '48px',
  },
  looseMd: {
    paddingLeft: '32px',
    paddingRight: '32px',
  },
  tightMd: {
    paddingLeft: '48px',
    paddingRight: '48px',
  },
  looseLg: {
    paddingLeft: '48px',
    paddingRight: '48px',
  },
  tightLg: {
    paddingLeft: '120px',
    paddingRight: '120px',
  },
  looseXl: {
    paddingLeft: '64px',
    paddingRight: '64px',
  },
  tightXl: {
    paddingLeft: '160px',
    paddingRight: '160px',
  },
};

const maxWidthProperty = css<ContainerProps>`
  --max-width: ${({ maxWidth, theme: { screens } }) => {
    if (!maxWidth) {
      return '100%';
    }

    const maxWidthMap: Record<MaxWidthValueType, CSSProperties['width']> = {
      s: screens.s.max,
      m: screens.m.max,
      l: screens.l.max,
      xl: screens.xl.min,
      'sidebar-layout': container.maxWidthSidebarLayout.maxWidth,
    };

    if (maxWidthMap[maxWidth]) {
      return maxWidthMap[maxWidth];
    }

    return '100%';
  }};
`;

const smStyles = css<ContainerProps>`
  ${({ variant }) => css<ContainerProps>`
    min-width: min(0, var(--max-width));
    padding-inline-start: max(
      ${variant === 'loose' ? container.looseSm.paddingLeft : container.tightSm.paddingLeft},
      env(safe-area-inset-left)
    );
    padding-inline-end: max(
      ${variant === 'loose' ? container.looseSm.paddingRight : container.tightSm.paddingRight},
      env(safe-area-inset-right)
    );
  `}
`;

const mdStyles = css<ContainerProps>`
  ${({ variant, fullWidth, theme: { screens } }) => css<ContainerProps>`
    min-width: min(${screens.m.min}, var(--max-width));
    padding-inline-start: max(
      calc(
        ${variant === 'loose' ? container.looseMd.paddingLeft : container.tightMd.paddingLeft} +
          ${fullWidth ? '0px' : fixedMenuWidth}
      ),
      env(safe-area-inset-left)
    );
    padding-inline-end: max(
      ${variant === 'loose' ? container.looseMd.paddingLeft : container.tightMd.paddingLeft},
      env(safe-area-inset-right)
    );
  `}
`;

const lgStyles = css<ContainerProps>`
  ${({ variant, fullWidth, theme: { screens } }) => css<ContainerProps>`
    min-width: min(${screens.l.min}, var(--max-width));
    padding-inline-start: max(
      calc(
        ${variant === 'loose' ? container.looseLg.paddingLeft : container.tightLg.paddingLeft} +
          ${fullWidth ? '0px' : fixedMenuWidth}
      ),
      env(safe-area-inset-left)
    );
    padding-inline-end: max(
      ${variant === 'loose' ? container.looseLg.paddingLeft : container.tightLg.paddingLeft},
      env(safe-area-inset-right)
    );
  `}
`;

const xlStyles = css<ContainerProps>`
  ${({ variant, fullWidth, theme: { screens } }) => css<ContainerProps>`
    min-width: min(${screens.l.min}, var(--max-width));
    padding-inline-start: max(
      calc(
        ${variant === 'loose' ? container.looseXl.paddingLeft : container.tightXl.paddingLeft} +
          ${fullWidth ? '0px' : fixedMenuWidth}
      ),
      env(safe-area-inset-left)
    );
    padding-inline-end: max(
      ${variant === 'loose' ? container.looseXl.paddingLeft : container.tightXl.paddingLeft},
      env(safe-area-inset-right)
    );
  `}
`;

export const ContainerRoot = styled.div<ContainerProps>(
  ({ alignItems, flexDirection, justifyContent, maxWidth, theme: { mediaQueries } }) => css`
    align-content: start;
    align-items: ${alignItems};
    box-sizing: border-box;
    display: flex;
    flex-direction: ${flexDirection};
    justify-content: ${justifyContent};
    list-style: none;
    margin: 0 auto;
    width: 100%;
    max-width: var(--max-width);
    padding-inline-start: max(${container.root.paddingInlineStart}, env(safe-area-inset-left));
    padding-inline-end: max(${container.root.paddingInlineEnd}, env(safe-area-inset-right));

    ${smStyles};

    ${mediaQueries.m} {
      ${!maxWidth || ['m', 'l', 'xl'].includes(maxWidth) ? mdStyles : ''}
    }

    ${mediaQueries.l} {
      ${!maxWidth || ['l', 'xl'].includes(maxWidth) ? lgStyles : ''}
    }

    ${mediaQueries.xl} {
      ${!maxWidth || ['xl'].includes(maxWidth) ? xlStyles : ''}
    }

    ${maxWidthProperty}
  `
);
